import { Component, DestroyRef, inject, input, type OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, type ValidationErrors, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { type WidgetDeviceChart, type WidgetGauge } from '../../../models';
import { DataPointOptionsComponent } from '../data-point';

@Component({
  imports: [DataPointOptionsComponent, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCardModule],
  selector: 'avs-gauge-options',
  templateUrl: './gauge-options.component.html',
})
export class GaugeOptionsComponent implements OnInit {
  changed = output<Partial<WidgetGauge>>();
  config = input<WidgetGauge>();

  protected datapoint = signal<Partial<WidgetDeviceChart> | null>(null);
  protected form!: FormGroup;
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  ngOnInit() {
    this.form = this.fb.group({
      criticalMax: [this.config()?.criticalMax ?? null, [Validators.required]],
      criticalMin: [this.config()?.criticalMin ?? null, [Validators.required]],
      normalMax: [this.config()?.normalMax ?? null, [Validators.required]],
      normalMin: [this.config()?.normalMin ?? null, [Validators.required, Validators.min(0)]],
      warningMax: [{ disabled: true, value: this.config()?.warningMax ?? null }, [Validators.required]],
      warningMin: [{ disabled: true, value: this.config()?.warningMin ?? null }, [Validators.required]],
    }, { validators: this.sequentialValidator });

    if (this.config()) {
      this.datapoint.set({
        datapointName: this.config()?.datapointName,
        deviceId: this.config()?.deviceId,
        floorId: this.config()?.floorId,
        locationId: this.config()?.locationId,
        roomId: this.config()?.roomId,
      });
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emitData();
      });

    const normalMaxControl = this.form.get('normalMax');

    normalMaxControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (normalMaxControl.valid) {
          this.form.get('warningMin')?.patchValue(value);
        }
      });

    const criticalMinControl = this.form.get('criticalMin');

    criticalMinControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (criticalMinControl.valid) {
          this.form.get('warningMax')?.patchValue(value);
        }
      });
  }

  protected setDatapoint(value: Partial<WidgetDeviceChart> | null) {
    this.datapoint.set(value);
    this.emitData();
  }

  private sequentialValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const gaugeValues = ['normalMin', 'normalMax', 'warningMin', 'warningMax', 'criticalMin', 'criticalMax'];

    const errors: { [key: string]: any; } = {};

    for (let i = 0; i < gaugeValues.length; i++) {
      const controlA = control.get(gaugeValues[i]);
      const controlB = control.get(gaugeValues[i - 1]);

      if (controlA?.value < controlB?.value) {
        controlA?.setErrors({ sequentialError: true });

        errors[gaugeValues[i]] = true;
      }
    }

    return Object.keys(errors).length ? { sequentialError: errors } : null;
  }

  private emitData() {
    const datapoint = this.datapoint();

    this.changed.emit(this.form.valid && datapoint ? {
      ...datapoint,
      ...this.form.getRawValue(),
    } : null);
  }
}
